import { useEffect, useState } from 'react'
import AnchoredPopover from '../../../components/AnchoredPopover'
import Sheet from '../../../components/Sheet'
import { photoSlotType, type CardPhotoSlot } from '../../../photo-slots'

/**
 * Лист «?» у фото-поля карточки тренда (18.10): что загрузить в это поле — текст типа поля из общего определения
 * (shared/photo-slots), подсказка админа, правила и два образца «подходит / не подходит». Десктоп — поповер у
 * кнопки «?», телефон — нижний лист (как ModelHelpSheet). Образцы — рисунки, не фото: лица людей не нужны.
 */
interface Props {
	open: boolean
	onClose: () => void
	anchorEl: HTMLElement | null
	slot: CardPhotoSlot
	/** Подпись поля (своя от админа или подпись типа). */
	label: string
}

/** Рисунок образца: silhouette по типу поля, «не подходит» — с обрезом кадра/лишним. */
function Sample({ type, good }: { type: string; good: boolean }) {
	const stroke = good ? 'var(--ps-good)' : 'var(--ps-bad)'
	const common = { fill: 'none', stroke, strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
	let body: React.ReactNode
	switch (type) {
		case 'face':
			body = good ? (
				<>
					<ellipse cx="40" cy="30" rx="13" ry="15" {...common} />
					<path d="M34 28h2M44 28h2M36 36q4 3 8 0" {...common} />
					<path d="M18 62q4-12 22-12t22 12" {...common} />
				</>
			) : (
				<>
					<ellipse cx="55" cy="30" rx="13" ry="15" {...common} />
					<path d="M50 28h2M60 28h2" {...common} />
					<path d="M44 22h20M46 40l-4 22M64 40l4 22" {...common} />
					<path d="M8 16h10M8 16v10" {...common} />
				</>
			)
			break
		case 'full_body':
			body = good ? (
				<>
					<circle cx="40" cy="12" r="6" {...common} />
					<path d="M32 22h16v18H32ZM34 40l-2 22M46 40l2 22M28 62h8M44 62h8" {...common} />
				</>
			) : (
				<>
					<circle cx="40" cy="14" r="7" {...common} />
					<path d="M30 25h20v22H30ZM32 47l-2 17M48 47l2 17" {...common} />
					<path d="M6 64h68" {...common} strokeDasharray="4 3" />
				</>
			)
			break
		case 'clothing':
			body = good ? (
				<path d="M28 16l-12 8 5 10 7-3v30h24V31l7 3 5-10-12-8a12 12 0 0 1-24 0Z" {...common} />
			) : (
				<>
					<path d="M46 16l-12 8 5 10 7-3v30h24V31l7 3 5-10-12-8a12 12 0 0 1-24 0Z" {...common} />
					<path d="M8 16h10M8 16v10M8 60h10M8 60v-10" {...common} />
				</>
			)
			break
		case 'object':
			body = good ? (
				<path d="M40 12 18 24v28l22 12 22-12V24Zm0 0v52M18 24l22 12 22-12" {...common} />
			) : (
				<>
					<path d="M26 40 14 46v14l12 6 12-6V46Zm22-14-10 5v12l10 5 10-5V31Z" {...common} />
					<path d="M60 46l-8 4v10l8 4 8-4V50Z" {...common} />
				</>
			)
			break
		default:
			body = good ? (
				<>
					<path d="M10 54l18-20 14 14 10-8 18 14" {...common} />
					<circle cx="56" cy="22" r="5" {...common} />
				</>
			) : (
				<>
					<path d="M10 54l18-20 14 14 10-8 18 14" {...common} />
					<circle cx="40" cy="26" r="6" {...common} />
					<path d="M32 36h16v16H32Z" {...common} />
				</>
			)
	}
	return (
		<svg viewBox="0 0 80 72" className="gm__slothelp-sample__svg" aria-hidden="true">
			{body}
		</svg>
	)
}

export default function PhotoSlotHelp({ open, onClose, anchorEl, slot, label }: Props) {
	const [isMobile, setIsMobile] = useState(
		() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
	)
	useEffect(() => {
		const mq = window.matchMedia('(max-width: 640px)')
		const fn = (e: MediaQueryListEvent) => setIsMobile(e.matches)
		mq.addEventListener('change', fn)
		return () => mq.removeEventListener('change', fn)
	}, [])

	const def = photoSlotType(slot.type)
	const body = (
		<div className="gm__slothelp">
			{slot.hint && <p className="gm__slothelp-hint">{slot.hint}</p>}
			{def && <p className="gm__slothelp-what">{def.help.what}</p>}
			{def && (
				<div className="gm__slothelp-samples">
					<div className="gm__slothelp-sample gm__slothelp-sample--good">
						<Sample type={def.key} good />
						<span>{def.help.good}</span>
					</div>
					<div className="gm__slothelp-sample gm__slothelp-sample--bad">
						<Sample type={def.key} good={false} />
						<span>{def.help.bad}</span>
					</div>
				</div>
			)}
			{def && (
				<ul className="gm__slothelp-rules">
					{def.help.rules.map((r) => (
						<li key={r}>{r}</li>
					))}
				</ul>
			)}
			<button type="button" className="gm__slothelp-ok" onClick={onClose}>
				Понятно
			</button>
		</div>
	)

	if (isMobile) {
		return (
			<Sheet open={open} onClose={onClose} variant="bottom" title={label} showHandle className="gm__slothelp-sheet">
				{body}
			</Sheet>
		)
	}
	return (
		<AnchoredPopover open={open} onClose={onClose} anchorEl={anchorEl} width={272} align="center" className="gm__slothelp-pop">
			<div className="gm__slothelp-title">{label}</div>
			{body}
		</AnchoredPopover>
	)
}
